import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  TextField,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import loginBackground from '../assets/login-background.jpg';
import logoWhiteText from '../assets/morgan-rural-tech-logo.png';
import logoBlueText from '../assets/morgan-rural-tech-logo-blue.png';
import { useThemeMode } from '../contexts/ThemeModeContext';

const Register: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const logo = mode === 'light' ? logoBlueText : logoWhiteText;
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await auth.register(email, password, fullName);
      navigate('/get-started');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${loginBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(15,17,23,0.72)',
          backgroundImage: `radial-gradient(ellipse at 50% 0%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`,
        }}
      />
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, md: 5 },
              width: '100%',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img src={logo} alt="Morgan Rural Tech" style={{ height: 36 }} />
              </Box>
              <Typography
                component="h1"
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}
              >
                Create an Account
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}>
                You'll be asked to verify your email and phone next
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleRegister} style={{ width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText="At least 8 characters"
                    disabled={isLoading}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={confirmPassword.length > 0 && confirmPassword !== password}
                    helperText={confirmPassword.length > 0 && confirmPassword !== password ? 'Passwords do not match' : ' '}
                    disabled={isLoading}
                    required
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={isLoading}
                    sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem', '&:disabled': { opacity: 0.6 } }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Creating account...
                      </>
                    ) : (
                      <>
                        <PersonAddIcon sx={{ mr: 1 }} />
                        Sign Up
                      </>
                    )}
                  </Button>
                </Box>
              </form>

              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
